import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PackageX, Clock, Package, RefreshCw, ExternalLink, AlertCircle } from "lucide-react";

/**
 * Widget Tableau de bord — Retards de restitution (admin / manager uniquement).
 *
 * Liste en temps réel les demandes de restitution (`equipment_return_requests`)
 * en statut `pending` ou `awaiting_controller` dont la date+heure de fin
 * prévue est dépassée. Polling toutes les 90 s. Le scheduler horaire backend
 * envoie déjà les notifications dédupliquées ; ce widget est purement
 * informatif/actionnable (lien direct vers la file d'attente).
 */
export default function OverdueReturnsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const { data } = await api.get("/equipment-returns/overdue");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 90_000);
    return () => clearInterval(id);
  }, [load]);

  const formatDelay = (mins) => {
    if (!mins || mins < 60) return `+${mins || 0} min`;
    if (mins < 60 * 24) return `+${Math.floor(mins / 60)} h`;
    return `+${Math.floor(mins / (60 * 24))} j`;
  };

  const statusLabel = (s) => (s === "awaiting_controller" ? "À contre-signer" : "Non signée par le collab");
  const statusCls = (s) =>
    s === "awaiting_controller"
      ? "bg-sky-50 text-sky-700 border-sky-200"
      : "bg-amber-50 text-amber-800 border-amber-200";

  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid="overdue-returns-widget">
      <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`h-7 w-7 inline-flex items-center justify-center rounded-sm flex-shrink-0 ${
              items.length > 0
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-zinc-50 text-zinc-400 border border-zinc-200"
            }`}
          >
            <PackageX size={14} />
          </span>
          <div className="min-w-0">
            <p className="label-uppercase">Retards de restitution</p>
            <p className="text-[11px] text-zinc-500 font-mono truncate">
              Demandes dont l'échéance prévue est dépassée
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`text-[11px] font-mono px-2 py-0.5 rounded-sm ${
              items.length > 0
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
            }`}
            data-testid="overdue-returns-count"
          >
            {items.length} en retard
          </span>
          <Link
            to="/equipements"
            className="text-[11px] font-mono text-zinc-500 hover:text-blue-700 inline-flex items-center gap-1"
            data-testid="overdue-returns-open-list"
            title="Ouvrir la file d'attente des restitutions"
          >
            <ExternalLink size={11} /> Voir
          </Link>
          <button
            onClick={load}
            className="text-zinc-500 hover:text-zinc-800 disabled:opacity-50"
            disabled={refreshing}
            title="Rafraîchir"
            data-testid="overdue-returns-refresh"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="p-5 text-sm text-zinc-500 font-mono">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="p-5 text-center" data-testid="overdue-returns-empty">
          <p className="text-sm text-zinc-700">Aucune restitution en retard.</p>
          <p className="text-[11px] text-zinc-500 font-mono mt-1">
            Toutes les demandes ouvertes sont dans la fenêtre de restitution prévue.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((r) => (
            <li
              key={r.id}
              className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`overdue-return-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {r.collaborateur_id ? (
                    <Link
                      to={`/collaborateurs/${r.collaborateur_id}/dossier`}
                      className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                      data-testid={`overdue-return-collab-link-${r.id}`}
                    >
                      {r.collaborateur_name || "Collaborateur"}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-zinc-900">
                      {r.collaborateur_name || "Collaborateur"}
                    </span>
                  )}
                  <span
                    className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${statusCls(r.status)}`}
                    data-testid={`overdue-return-status-${r.id}`}
                  >
                    {statusLabel(r.status)}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                  <p className="inline-flex items-center gap-1.5">
                    <Package size={11} className="text-zinc-400" />
                    {r.designation || "Matériel"}
                    {r.ref_id && <span className="text-zinc-400">· {r.ref_id}</span>}
                  </p>
                  <p className="inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-zinc-400" />
                    Prévue le {r.return_date}
                    {r.return_end_time && <span className="text-zinc-400">→ {r.return_end_time}</span>}
                  </p>
                  {r.requested_by_name && (
                    <p className="inline-flex items-center gap-1.5">
                      <AlertCircle size={11} className="text-zinc-400" />
                      Demandée par {r.requested_by_name}
                    </p>
                  )}
                </div>
              </div>
              <span
                className="text-[11px] font-mono font-semibold px-2 py-1 rounded-sm bg-red-50 text-red-700 border border-red-200 flex-shrink-0"
                data-testid={`overdue-return-delay-${r.id}`}
              >
                {formatDelay(r.delay_minutes)}
              </span>
              <Link
                to="/equipements"
                state={{ openTab: "tab-equip-returns", returnId: r.id }}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm bg-zinc-900 text-white hover:bg-zinc-800 flex-shrink-0 transition-colors"
                title="Ouvrir la file d'attente des restitutions"
                data-testid={`overdue-return-open-${r.id}`}
              >
                <ExternalLink size={12} />
                Traiter
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
